/*
  LIOR - collection page in the style of iOS 27 (assets/lior-ios-collection.css)

  The theme stays in charge of the data: its filter form, its sort inputs and its
  infinite scroll. This file only drives what you see and touch:
    - the filter sheet on phones: a spring that follows the finger, can be
      grabbed mid-flight, and is thrown closed with the release velocity
    - the floating toolbar on phones (filter + sort) that shrinks to its icons
      while you scroll down, as Safari's bar does
    - the sort menu, which grows out of the button that opened it
    - cards rising into place as they come on screen (once per product)
    - the theme's infinite scroll adds the next page without rebuilding the grid
      (it used to turn every card on the page into HTML and back, which made apps
      add their swatches to the same card again and again)
    - a guard that keeps one swatch group per card, whatever an app adds, laid
      out as a single row under the photo
    - Loox's pop-up review card lifted above the floating toolbar on phones
    - the light under the finger on glass controls
  It talks to the theme only by clicking the theme's own buttons and inputs, and
  falls back to doing the same thing itself if the theme does not respond.
  (The large title above the grid has its own files: lior-ios-banner-hero.*)
*/
(function () {
  'use strict';

  if (window.__liorIosCollection) return;
  window.__liorIosCollection = true;

  var D = document;
  var H = D.documentElement;
  var W = window;
  var mqPhone = W.matchMedia('(max-width: 767px)');
  var mqReduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  var RTL = (H.getAttribute('dir') || D.body.getAttribute('dir') || 'rtl').toLowerCase() === 'rtl';

  function reduce() { return mqReduce.matches; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function now() { return W.performance ? W.performance.now() : Date.now(); }

  /* ------------------------------------------------------------------------
     Springs (damping ratio + response, as Apple describes them), re-targetable
     at any moment from the current value and velocity
     ------------------------------------------------------------------------ */
  function Spring(value) {
    this.x = value || 0;
    this.v = 0;
    this.t = this.x;
    this.config(1, 0.4);
  }
  Spring.prototype.config = function (zeta, response) {
    this.zeta = zeta;
    this.w = (2 * Math.PI) / response;
    return this;
  };
  Spring.prototype.to = function (target, velocity) {
    this.t = target;
    if (typeof velocity === 'number' && isFinite(velocity)) this.v = velocity;
    return this;
  };
  Spring.prototype.step = function (dt) {
    var n = Math.max(1, Math.ceil(dt / 0.004));
    var h = dt / n;
    for (var i = 0; i < n; i++) {
      var a = -this.w * this.w * (this.x - this.t) - 2 * this.zeta * this.w * this.v;
      this.v += a * h;
      this.x += this.v * h;
    }
  };
  Spring.prototype.settled = function (eps, veps) {
    return Math.abs(this.x - this.t) < eps && Math.abs(this.v) < veps;
  };

  /* One display-synced loop for everything that moves */
  var Loop = {
    jobs: [],
    raf: 0,
    last: 0,
    add: function (job) {
      if (Loop.jobs.indexOf(job) === -1) Loop.jobs.push(job);
      if (!Loop.raf) {
        Loop.last = now();
        Loop.raf = W.requestAnimationFrame(Loop.tick);
      }
    },
    remove: function (job) {
      var i = Loop.jobs.indexOf(job);
      if (i !== -1) Loop.jobs.splice(i, 1);
    },
    tick: function (t) {
      var dt = clamp((t - Loop.last) / 1000, 0.001, 0.05);
      Loop.last = t;
      var jobs = Loop.jobs.slice();
      for (var i = 0; i < jobs.length; i++) {
        if (jobs[i](dt) === false) Loop.remove(jobs[i]);
      }
      Loop.raf = Loop.jobs.length ? W.requestAnimationFrame(Loop.tick) : 0;
    }
  };

  /* Apple's momentum projection (Designing Fluid Interfaces) */
  function project(velocity) {
    var rate = 0.998;
    return ((velocity / 1000) * rate) / (1 - rate);
  }

  function rubber(over, dim) {
    var c = 0.55;
    return (over * dim * c) / (dim + c * Math.abs(over));
  }

  /* Velocity over the last 100 ms of samples [time, position] */
  function velocityOf(samples) {
    if (samples.length < 2) return 0;
    var last = samples[samples.length - 1];
    var first = samples[0];
    for (var i = samples.length - 1; i >= 0; i--) {
      if (last[0] - samples[i][0] > 100) break;
      first = samples[i];
    }
    var dt = last[0] - first[0];
    return dt > 0 ? ((last[1] - first[1]) / dt) * 1000 : 0;
  }

  var SPRING_EASE = 'linear(0, 0.0265, 0.0902, 0.1733, 0.2642, 0.3553, 0.4421, 0.5221, 0.5939, 0.6574, 0.7127, 0.7602, 0.8008, 0.8352, 0.8641, 0.8883, 0.9084, 0.9251, 0.9389, 0.9502, 0.9596, 0.9672, 0.9734, 0.9785, 0.9826, 0.986, 0.9887, 0.9909, 0.9927, 0.9941, 0.9953, 0.9962, 0.997, 0.9976, 0.9981, 0.9985, 0.9988, 0.999, 0.9992, 0.9994, 1)';
  function easing() {
    return W.CSS && CSS.supports && CSS.supports('transition-timing-function', 'linear(0, 1)') ? SPRING_EASE : 'cubic-bezier(0.32, 0.72, 0, 1)';
  }

  var ICON = {
    filter: '<svg class="lic-ic" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16M7 12h10M10 17h4"/></svg>',
    sort: '<svg class="lic-ic" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 20V5M3.5 8.5 7 5l3.5 3.5M17 4v15m-3.5-3.5L17 19l3.5-3.5"/></svg>',
    check: '<svg class="lic-ic" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>'
  };

  function productsWord(n) {
    return n === 1 ? 'מוצר אחד' : n + ' מוצרים';
  }

  /* ------------------------------------------------------------------------
     Mount on the collection section
     ------------------------------------------------------------------------ */
  function mount(root) {
    var st = { root: root, offs: [], alive: true };
    function on(el, type, fn, opt) {
      el.addEventListener(type, fn, opt);
      st.offs.push(function () { el.removeEventListener(type, fn, opt); });
    }
    function q(sel) { return root.querySelector(sel); }
    function sidebar() { return q('.filter--sidebar'); }
    function form() { return q('#filter-sorting-form') || q('form.filter--sidebar-content'); }

    /* How many products the current view holds (the theme keeps this in the bar) */
    function total() {
      var c = q('[data-total-products-count]');
      if (c) {
        var m = (c.textContent || '').match(/(\d[\d,.]*)\s*\/\s*(\d[\d,.]*)/);
        if (m) return parseInt(m[2].replace(/[^\d]/g, ''), 10);
      }
      var h = q('[data-coll-products-count]');
      if (h) {
        var n = parseInt(h.getAttribute('data-coll-products-count'), 10);
        if (!isNaN(n)) return n;
      }
      var cards = root.querySelectorAll('.main-collection--products-list > .product--card').length;
      return q('.empty--card') ? 0 : cards;
    }

    /* ======================= Cards: rise into place ======================= */
    var seen = new Set();
    var order = [];
    var watched = new Set();
    function cardKey(card) {
      var inner = card.querySelector('[data-product-card]');
      if (inner && inner.getAttribute('data-id')) return inner.getAttribute('data-id');
      var a = card.querySelector('a[href]');
      return a ? a.getAttribute('href') : '';
    }
    function reveal(card, i) {
      var d = reduce() ? 0 : Math.min(i, 7) * 55;
      card.style.setProperty('--lic-d', d + 'ms');
      card.classList.add('lic-in');
      seen.add(cardKey(card));
      setTimeout(function () { card.style.removeProperty('--lic-d'); }, d + 1000);
    }
    var io = 'IntersectionObserver' in W ? new IntersectionObserver(function (entries) {
      var entering = [];
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          entering.push(e.target);
          io.unobserve(e.target);
          watched.delete(e.target);
        }
      });
      entering.sort(function (a, b) {
        var ra = a.getBoundingClientRect();
        var rb = b.getBoundingClientRect();
        return Math.round(ra.top - rb.top) || (RTL ? rb.left - ra.left : ra.left - rb.left);
      });
      entering.forEach(reveal);
    }, { rootMargin: '0px 0px -4% 0px', threshold: 0.01 }) : null;

    function showNow(card) {
      card.classList.add('lic-in', 'lic-now');
      seen.add(cardKey(card));
      W.requestAnimationFrame(function () {
        W.requestAnimationFrame(function () { card.classList.remove('lic-now'); });
      });
    }

    function scanCards(first) {
      var cards = Array.prototype.slice.call(root.querySelectorAll('.main-collection--products-list > .product--card'));
      var ids = cards.map(cardKey);
      var appended = order.length > 0 && order.every(function (id, k) { return ids[k] === id; });
      if (!first && order.length && !appended) seen.clear();
      order = ids;
      watched.forEach(function (el) {
        if (!el.isConnected) {
          if (io) io.unobserve(el);
          watched.delete(el);
        }
      });
      var vh = W.innerHeight;
      cards.forEach(function (card) {
        if (card.classList.contains('lic-in') || watched.has(card)) return;
        if (!io || seen.has(cardKey(card))) return showNow(card);
        if (first) {
          var r = card.getBoundingClientRect();
          if (r.top < vh && r.bottom > 0) return showNow(card);
        }
        watched.add(card);
        io.observe(card);
      });
    }

    /* ======================= The next page: added, never rebuilt =======================
       The theme's infinite scroll (theme.OnScroll in assets/magnet.js) adds a page with
       `list.innerHTML += next.innerHTML`. That turns every card already on the page into
       HTML and back: images load again, the page jumps, and whatever an app had added to
       a card comes back as a lifeless copy while the app sees a brand-new card and adds
       its part once more. With the swatches of SA Variants that is one more group per
       card on every page loaded. While a page is on its way, the list lends its marker
       attribute to an inert <template>; the theme's `+=` lands there, and the new cards
       then move into the list in one step. The cards already on the page are untouched. */
    var sink = null;
    function beforeAppend() {
      if (sink || !st.alive) return;
      var list = q('[main-collection-products]');
      if (!list || D.querySelector('[main-collection-products]') !== list) return;
      var tpl = D.createElement('template');
      tpl.setAttribute('main-collection-products', '');
      tpl.setAttribute('data-lic-next', '');
      list.removeAttribute('main-collection-products');
      list.parentNode.insertBefore(tpl, list.nextSibling);
      var mine = { tpl: tpl, list: list };
      sink = mine;
      var watch = new MutationObserver(settle);
      watch.observe(tpl.content, { childList: true });
      /* if the theme never writes (an error on its side), give the list its marker back */
      var t = setTimeout(settle, 0);
      function settle() {
        watch.disconnect();
        clearTimeout(t);
        if (sink !== mine) return;
        sink = null;
        list.setAttribute('main-collection-products', '');
        var frag = tpl.content;
        tpl.remove();
        if (frag.firstChild && list.isConnected) list.appendChild(frag);
      }
    }
    st.offs.push(function () {
      if (!sink) return;
      sink.list.setAttribute('main-collection-products', '');
      sink.tpl.remove();
      sink = null;
    });

    /* ======================= App swatches: one row per card =======================
       Apps such as SA Variants put a group of swatches into each card from their own
       script. Whatever makes such a script run twice (a rebuilt grid, a second init, a
       re-render), a card keeps one group: copies with the same signature are removed,
       and the one an app added last stays, since that is the one it is still talking
       to. The group is marked (.lic-sw, .lic-sw-row, .lic-sw-i, .lic-sw-dot) so the CSS
       can lay it out as one tidy row under the photo that never spills into the next
       card. Nothing is moved or re-created, so an app's own hover and tap handlers
       keep working. */
    var SW_RE = /swatch|starapps|variant-?king|vkcl|combined-?listing|(^|[^a-z0-9])(sa|vk)[-_]/i;
    var SW_OWN = '.product--variant-picker-list, .collection-size-availability, .card-product-linked-product--options, .product-size-variant-list, .product--card-badges, quick-view, .product--card-detail-button';
    var SW_KIDS = '.product--card-image, .product--card-detail';
    var SW_SKIP = /^(SCRIPT|STYLE|TEMPLATE|LINK|META|NOSCRIPT)$/;
    var SW_STATE = /^(is|has)[-_]|active|selected|current|checked|hover|focus|loaded|loading|init|ready|visible|hidden|show|open|disabled|sold|^lic-/i;
    var swSeq = 0;
    var swStamp = new WeakMap();
    var swDue = new Set();
    var swQueued = false;
    var swDrops = new WeakMap();

    function addCls(el, c) {
      if (!el.classList.contains(c)) el.classList.add(c);
    }
    function swTest(el) {
      if (SW_RE.test(el.tagName + ' ' + (el.getAttribute('class') || '') + ' ' + (el.id || ''))) return true;
      var at = el.attributes;
      for (var i = 0; i < at.length; i++) {
        if (/^data-(sa[-_]|starapps|vk[-_]|vkcl|swatch)/i.test(at[i].name)) return true;
      }
      return false;
    }
    /* The groups in a card: elements that look like a swatch app's, outermost first, and
       anything an app put between the photo and the text (the "After image" slot) */
    function swGroups(card) {
      var inner = card.querySelector('[data-product-card]') || card;
      var out = [];
      function add(el) {
        for (var i = 0; i < out.length; i++) if (out[i] === el || out[i].contains(el)) return;
        out = out.filter(function (g) { return !el.contains(g); });
        out.push(el);
      }
      var all = inner.getElementsByTagName('*');
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        if (SW_SKIP.test(el.tagName) || el.closest(SW_OWN)) continue;
        if (swTest(el)) add(el);
      }
      for (var c = inner.firstElementChild; c; c = c.nextElementSibling) {
        if (!SW_SKIP.test(c.tagName) && !c.matches(SW_KIDS) && !c.matches(SW_OWN)) add(c);
      }
      return out;
    }
    function swSig(el) {
      var t = (el.getAttribute('class') || '').split(/\s+/).filter(function (k) { return k && !SW_STATE.test(k); });
      return el.tagName + '|' + t.map(function (k) { return k.replace(/\d+/g, '#'); }).sort().join('.');
    }
    /* how recently an app wrote this group (0: it came with the card) */
    function swAge(g, card) {
      var a = swStamp.get(g) || 0;
      for (var n = g.parentElement; n && n !== card; n = n.parentElement) a = Math.max(a, swStamp.get(n) || 0);
      var d = g.getElementsByTagName('*');
      for (var i = 0; i < d.length; i++) a = Math.max(a, swStamp.get(d[i]) || 0);
      return a;
    }
    function swDrop(card, g) {
      var t = now();
      var log = (swDrops.get(card) || []).filter(function (x) { return t - x < 4000; });
      log.push(t);
      swDrops.set(card, log);
      /* something keeps putting it back: hide the extra copy rather than fight over it */
      if (log.length > 6) return addCls(g, 'lic-sw-dup');
      g.remove();
    }
    function swLeaves(g) {
      var all = g.getElementsByTagName('*');
      var out = [];
      var i;
      for (i = 0; i < all.length; i++) {
        var st2 = all[i].getAttribute('style') || '';
        if (all[i].tagName === 'IMG' || /background|--[\w-]*(colou?r|image|swatch|bg)/i.test(st2)) out.push(all[i]);
      }
      if (!out.length) {
        for (i = 0; i < all.length; i++) {
          if (!all[i].firstElementChild && /swatch|colou?r|dot|circle|chip|option|value/i.test(all[i].getAttribute('class') || '')) out.push(all[i]);
        }
      }
      return out.filter(function (el) {
        for (var k = 0; k < out.length; k++) if (out[k] !== el && out[k].contains(el)) return false;
        return true;
      });
    }
    function swMark(g) {
      addCls(g, 'lic-sw');
      var leaves = swLeaves(g);
      if (!leaves.length || leaves.indexOf(g) !== -1) return;
      var row;
      var items = [];
      if (leaves.length === 1) {
        var item = leaves[0];
        var up = item.parentElement;
        if (up && up !== g && up.childElementCount === 1) item = up;
        row = item.parentElement || g;
        items.push(item);
      } else {
        row = leaves[0].parentElement;
        while (row && row !== g && !leaves.every(function (l) { return row.contains(l); })) row = row.parentElement;
        if (!row || !g.contains(row)) row = g;
        leaves.forEach(function (l) {
          var n = l;
          while (n.parentElement && n.parentElement !== row) n = n.parentElement;
          if (items.indexOf(n) === -1) items.push(n);
        });
      }
      addCls(row, 'lic-sw-row');
      items.forEach(function (it) { addCls(it, 'lic-sw-i'); });
      leaves.forEach(function (l) { addCls(l, 'lic-sw-dot'); });
      for (var c = row.firstElementChild; c; c = c.nextElementSibling) {
        if (items.indexOf(c) === -1 && !c.querySelector('.lic-sw-dot') && (c.textContent || '').trim()) addCls(c, 'lic-sw-more');
      }
    }
    function swTidy(card) {
      if (!card.isConnected) return;
      var groups = swGroups(card);
      if (!groups.length) return;
      var bySig = Object.create(null);
      groups.forEach(function (g) {
        var swatchy = swTest(g) || !!g.querySelector('[style*="background"], img, svg, input[type="radio"]');
        /* other app blocks only count as copies when their words match too */
        var k = swSig(g) + (swatchy ? '' : '|' + (g.textContent || '').replace(/\s+/g, ' ').trim());
        (bySig[k] = bySig[k] || { swatchy: swatchy, gs: [] }).gs.push(g);
      });
      Object.keys(bySig).forEach(function (k) {
        var gs = bySig[k].gs;
        var keep = gs[0];
        if (gs.length > 1) {
          var best = -1;
          gs.forEach(function (g) {
            var age = g.classList.contains('lic-sw-dup') ? -0.5 : swAge(g, card);
            if (age > best) {
              best = age;
              keep = g;
            }
          });
          gs.forEach(function (g) { if (g !== keep) swDrop(card, g); });
        }
        if (keep.classList.contains('lic-sw-dup')) keep.classList.remove('lic-sw-dup');
        /* a group the app keeps hidden (no siblings, say) is left as it is */
        if (bySig[k].swatchy && W.getComputedStyle(keep).display !== 'none') swMark(keep);
      });
    }
    function swFlush() {
      swQueued = false;
      if (!st.alive) return;
      var cards = [];
      swDue.forEach(function (c) { cards.push(c); });
      swDue.clear();
      cards.forEach(swTidy);
      /* a soft edge on rows that hold more than fits */
      W.requestAnimationFrame(function () {
        cards.forEach(function (c) {
          c.querySelectorAll('.lic-sw').forEach(function (g) {
            var more = g.scrollWidth > g.clientWidth + 1;
            if (g.classList.contains('lic-sw-x') !== more) g.classList.toggle('lic-sw-x', more);
          });
        });
      });
    }
    function swQueue(card) {
      swDue.add(card);
      if (swQueued) return;
      swQueued = true;
      Promise.resolve().then(swFlush);
    }
    var swMO = new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) {
        var added = recs[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          var card = n.closest('.product--card');
          if (card && card !== n) {
            /* something was put inside a card: an app at work */
            swStamp.set(n, ++swSeq);
            swQueue(card);
          } else if (card) {
            swQueue(card);
          } else {
            var cs = n.getElementsByClassName('product--card');
            for (var k = 0; k < cs.length; k++) swQueue(cs[k]);
          }
        }
      }
    });
    swMO.observe(root, { childList: true, subtree: true });
    st.offs.push(function () { swMO.disconnect(); });
    Array.prototype.forEach.call(root.getElementsByClassName('product--card'), swQueue);

    /* ======================= Glass light under the finger ======================= */
    var pressed = null;
    function release() {
      if (pressed) pressed.classList.remove('is-pressed');
      pressed = null;
    }
    on(D, 'pointerdown', function (e) {
      var b = e.target.closest && e.target.closest('.lic-glass-btn, .lic .filter-toggle-button, .lic .drawer-close');
      if (!b || (e.button && e.button !== 0)) return;
      var r = b.getBoundingClientRect();
      b.style.setProperty('--lic-px', (e.clientX - r.left) + 'px');
      b.style.setProperty('--lic-py', (e.clientY - r.top) + 'px');
      release();
      pressed = b;
      b.classList.add('is-pressed');
    }, true);
    on(W, 'pointerup', release, true);
    on(W, 'pointercancel', release, true);
    on(D, 'pointermove', function (e) {
      if (!pressed) return;
      var r = pressed.getBoundingClientRect();
      var pad = 24;
      if (e.clientX < r.left - pad || e.clientX > r.right + pad || e.clientY < r.top - pad || e.clientY > r.bottom + pad) return release();
      pressed.style.setProperty('--lic-px', (e.clientX - r.left) + 'px');
      pressed.style.setProperty('--lic-py', (e.clientY - r.top) + 'px');
    }, { passive: true });

    /* ======================= Sort ======================= */
    function sortInputs() {
      var f = form();
      if (!f) return null;
      var radios = Array.prototype.slice.call(f.querySelectorAll('input[type="radio"][name="sort_by"]'));
      if (radios.length) {
        return {
          kind: 'radio',
          items: radios.map(function (r) {
            var label = r.getAttribute('data-name');
            if (!label) {
              var l = r.id ? f.querySelector('label[for="' + r.id + '"]') : null;
              label = l ? l.textContent.trim() : r.value;
            }
            return { value: r.value, name: label, checked: r.checked, el: r };
          })
        };
      }
      var sel = f.querySelector('select[name="sort_by"]');
      if (sel) {
        return {
          kind: 'select',
          sel: sel,
          items: Array.prototype.map.call(sel.options, function (o) {
            return { value: o.value, name: o.textContent.trim(), checked: o.selected, el: o };
          })
        };
      }
      return null;
    }

    var menu = D.createElement('div');
    menu.className = 'lic-menu';
    menu.hidden = true;
    menu.setAttribute('dir', RTL ? 'rtl' : 'ltr');
    menu.innerHTML = '<div class="lic-menu-panel" role="menu" aria-label="מיון לפי" tabindex="-1"><p class="lic-menu-title" aria-hidden="true">מיון לפי</p><ul class="lic-menu-list" role="none"></ul></div>';
    D.body.appendChild(menu);
    st.offs.push(function () { menu.remove(); });
    var panel = menu.firstChild;
    var list = panel.querySelector('.lic-menu-list');
    var mp = new Spring(0);
    var menuOpen = false;
    var menuAnchor = null;

    function paintMenu() {
      var p = mp.x;
      var s = 0.3 + 0.7 * p;
      panel.style.setProperty('--lic-ms', s.toFixed(4));
      panel.style.setProperty('--lic-mo', clamp(p * 1.8, 0, 1).toFixed(3));
      var b = (1 - clamp(p, 0, 1)) * 10;
      if (b > 0.05) panel.style.setProperty('--lic-mf', 'blur(' + b.toFixed(2) + 'px)');
      else panel.style.removeProperty('--lic-mf');
    }
    function menuJob(dt) {
      mp.step(dt);
      if (mp.settled(0.001, 0.02)) {
        mp.x = mp.t;
        mp.v = 0;
        paintMenu();
        if (!menuOpen) menu.hidden = true;
        return false;
      }
      paintMenu();
    }
    function buildMenu() {
      var inp = sortInputs();
      if (!inp) return false;
      list.innerHTML = '';
      inp.items.forEach(function (it) {
        var li = D.createElement('li');
        li.setAttribute('role', 'none');
        var b = D.createElement('button');
        b.type = 'button';
        b.className = 'lic-menu-item';
        b.setAttribute('role', 'menuitemradio');
        b.setAttribute('aria-checked', it.checked ? 'true' : 'false');
        b.setAttribute('data-value', it.value);
        b.tabIndex = -1;
        b.innerHTML = ICON.check + '<span></span>';
        b.lastChild.textContent = it.name;
        li.appendChild(b);
        list.appendChild(li);
      });
      return true;
    }
    function placeMenu(anchor) {
      panel.style.left = '0px';
      panel.style.top = '0px';
      var r = anchor.getBoundingClientRect();
      var pw = panel.offsetWidth;
      var ph = panel.offsetHeight;
      var vw = W.innerWidth;
      var vh = W.innerHeight;
      var m = 12;
      var x;
      if (anchor.closest('.lic-dock')) x = r.left + r.width / 2 - pw / 2;
      else x = RTL ? r.left : r.right - pw;
      x = clamp(x, m, Math.max(m, vw - pw - m));
      var below = r.top + r.height / 2 < vh * 0.5;
      var y = below ? r.bottom + 8 : r.top - 8 - ph;
      y = clamp(y, m, Math.max(m, vh - ph - m));
      panel.style.left = x + 'px';
      panel.style.top = y + 'px';
      panel.style.setProperty('--lic-ox', clamp(r.left + r.width / 2 - x, 0, pw).toFixed(1) + 'px');
      panel.style.setProperty('--lic-oy', clamp(r.top + r.height / 2 - y, -20, ph + 20).toFixed(1) + 'px');
    }
    function items() { return Array.prototype.slice.call(list.querySelectorAll('.lic-menu-item')); }
    function openMenu(anchor, viaKeyboard) {
      if (!buildMenu()) return;
      menuAnchor = anchor;
      menu.hidden = false;
      menuOpen = true;
      menu.classList.add('is-open');
      placeMenu(anchor);
      anchor.setAttribute('aria-expanded', 'true');
      if (reduce()) {
        mp.x = 1;
        mp.v = 0;
        mp.t = 1;
        paintMenu();
      } else {
        if (mp.x <= 0.001) mp.x = 0;
        mp.config(0.74, 0.4).to(1);
        Loop.add(menuJob);
      }
      var its = items();
      var cur = its.filter(function (b) { return b.getAttribute('aria-checked') === 'true'; })[0] || its[0];
      if (cur) {
        cur.tabIndex = 0;
        if (viaKeyboard) cur.focus({ preventScroll: true });
        else panel.focus({ preventScroll: true });
      }
    }
    function closeMenu(returnFocus) {
      if (!menuOpen) return;
      menuOpen = false;
      menu.classList.remove('is-open');
      if (menuAnchor) menuAnchor.setAttribute('aria-expanded', 'false');
      if (returnFocus && menuAnchor) menuAnchor.focus({ preventScroll: true });
      if (reduce()) {
        mp.x = mp.t = 0;
        mp.v = 0;
        paintMenu();
        menu.hidden = true;
      } else {
        mp.config(1, 0.24).to(0);
        Loop.add(menuJob);
      }
    }
    function sortLabel(name) {
      D.querySelectorAll('[data-lic-sort-label]').forEach(function (el) { el.textContent = name; });
    }

    /* If the theme ignores the change, load the sorted page ourselves */
    var fallbackTimer = 0;
    function armFallback(value) {
      clearTimeout(fallbackTimer);
      var t0 = now();
      var search = W.location.search;
      var grid = q('[data-collections-grid]');
      var changed = false;
      var mo = grid ? new MutationObserver(function () { changed = true; }) : null;
      if (mo) mo.observe(grid, { childList: true, subtree: true });
      fallbackTimer = setTimeout(function () {
        if (mo) mo.disconnect();
        if (!st.alive || changed || W.location.search !== search) return;
        var busy = (W.performance && performance.getEntriesByType ? performance.getEntriesByType('resource') : []).some(function (en) {
          return (en.initiatorType === 'fetch' || en.initiatorType === 'xmlhttprequest') && en.startTime >= t0;
        });
        if (busy) return;
        var f = form();
        var params = new URLSearchParams();
        if (f) {
          new FormData(f).forEach(function (v, k) {
            if (typeof v === 'string' && v !== '' && k !== 'sort_by') params.append(k, v);
          });
        }
        params.set('sort_by', value);
        W.location.assign(W.location.pathname + '?' + params.toString());
      }, 3500);
    }
    function chooseSort(value) {
      var inp = sortInputs();
      if (!inp) return;
      var it = inp.items.filter(function (i) { return i.value === value; })[0];
      closeMenu(false);
      if (!it || it.checked) return;
      sortLabel(it.name);
      armFallback(value);
      if (inp.kind === 'radio') {
        it.el.click();
      } else {
        inp.sel.value = value;
        inp.sel.dispatchEvent(new Event('input', { bubbles: true }));
        inp.sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    on(menu, 'click', function (e) {
      var b = e.target.closest('.lic-menu-item');
      if (b) return chooseSort(b.getAttribute('data-value'));
      if (!e.target.closest('.lic-menu-panel')) closeMenu(false);
    });
    on(menu, 'pointerdown', function (e) {
      if (!e.target.closest('.lic-menu-panel')) {
        e.preventDefault();
        closeMenu(false);
      }
    });
    on(menu, 'keydown', function (e) {
      var its = items();
      var i = its.indexOf(D.activeElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu(true);
      } else if (e.key === 'Tab') {
        closeMenu(true);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        var n = e.key === 'Home' ? 0 : e.key === 'End' ? its.length - 1 : (i + (e.key === 'ArrowDown' ? 1 : -1) + its.length) % its.length;
        if (i === -1 && e.key === 'ArrowUp') n = its.length - 1;
        its.forEach(function (b, k) { b.tabIndex = k === n ? 0 : -1; });
        if (its[n]) its[n].focus({ preventScroll: true });
      }
    });
    on(W, 'resize', function () { if (menuOpen) closeMenu(false); });
    on(W, 'scroll', function () { if (menuOpen && menuAnchor && !menuAnchor.closest('.lic-dock')) placeMenu(menuAnchor); }, { passive: true });

    on(D, 'click', function (e) {
      var b = e.target.closest && e.target.closest('[data-lic-sort], [data-lic-dock-sort]');
      if (!b) return;
      e.preventDefault();
      if (menuOpen && menuAnchor === b) closeMenu(false);
      else openMenu(b, e.detail === 0);
    });

    function refreshSort() {
      var inp = sortInputs();
      root.querySelectorAll('[data-lic-sort]').forEach(function (b) {
        if (!inp) return;
        if (b.hidden) {
          b.hidden = false;
          if (!reduce() && b.animate) {
            b.animate([
              { opacity: 0, scale: '0.8', filter: 'blur(4px)' },
              { opacity: 1, scale: '1', filter: 'blur(0px)' }
            ], { duration: 520, easing: easing() });
          }
        }
      });
      if (inp) {
        var cur = inp.items.filter(function (i) { return i.checked; })[0];
        if (cur) {
          D.querySelectorAll('[data-lic-sort-label]').forEach(function (el) {
            if (el.textContent !== cur.name) el.textContent = cur.name;
          });
        }
      }
    }

    /* ======================= Filter sheet (phones) ======================= */
    var sp = new Spring(0);
    var sheetOpen = false;
    var closedY = 900;
    var lastContent = null;
    var ignoreActiveUntil = 0;
    var dragging = false;
    var drag = { y0: 0, s0: 0, samples: [], mode: '', id: null };

    function parts() {
      var sb = sidebar();
      if (!sb) return null;
      return {
        sb: sb,
        content: sb.querySelector('.filter--sidebar-content'),
        overlay: sb.querySelector('.filter--sidebar-overlay'),
        header: sb.querySelector('.filter--sidebar-header'),
        cols: sb.querySelector('.filter--columns')
      };
    }
    function measure(p) {
      if (p && p.content) closedY = Math.max(240, p.content.offsetHeight + 48);
    }
    function paintSheet() {
      var p = parts();
      if (!p || !p.content) return;
      p.content.style.setProperty('--lic-y', sp.x.toFixed(2) + 'px');
      if (p.overlay) p.overlay.style.setProperty('--lic-dim', clamp(1 - sp.x / closedY, 0, 1).toFixed(3));
    }
    function sheetJob(dt) {
      if (dragging) return false;
      sp.step(dt);
      if (sp.settled(0.4, 6)) {
        sp.x = sp.t;
        sp.v = 0;
        paintSheet();
        return false;
      }
      paintSheet();
    }
    function ctaText(btn) {
      var n = total();
      var txt = n > 0 ? 'הצגת ' + productsWord(n) : 'אין מוצרים מתאימים';
      if (btn.textContent !== txt) btn.textContent = txt;
      btn.setAttribute('aria-disabled', n > 0 ? 'false' : 'true');
    }
    function chrome(p) {
      if (!p.content) return;
      var foot = p.content.querySelector('.lic-sheet-foot');
      if (!foot) {
        foot = D.createElement('div');
        foot.className = 'lic-sheet-foot';
        foot.innerHTML = '<button type="button" class="lic-sheet-cta lic-glass-btn" data-lic-cta></button>';
        p.content.appendChild(foot);
      }
      ctaText(foot.firstChild);
    }
    function setSheet(open, velocity) {
      var p = parts();
      if (!p || !p.content) return;
      if (open === sheetOpen) return;
      sheetOpen = open;
      H.classList.toggle('lic-sheet-open', open);
      if (open) bindTouch(true);
      else if (drag.mode !== 'touch') bindTouch(false);
      measure(p);
      if (open) {
        if (!p.content.style.getPropertyValue('--lic-y')) {
          sp.x = closedY;
          sp.v = 0;
        }
        if (reduce()) {
          sp.x = sp.t = 0;
          sp.v = 0;
          paintSheet();
        } else {
          sp.config(0.86, 0.46).to(0);
          Loop.add(sheetJob);
        }
        if (p.overlay) p.overlay.style.touchAction = 'none';
      } else {
        if (reduce()) {
          sp.x = sp.t = closedY;
          sp.v = 0;
          paintSheet();
        } else {
          sp.config(1, 0.34).to(closedY, velocity);
          Loop.add(sheetJob);
        }
        if (p.overlay) p.overlay.style.touchAction = '';
        var a = D.activeElement;
        if (a && p.sb.contains(a)) {
          var back = D.querySelector('[data-lic-dock-filter]') || q('[data-filters-mobile]');
          if (back) back.focus({ preventScroll: true });
        }
      }
    }
    function syncSheet() {
      if (!mqPhone.matches) {
        if (sheetOpen) {
          sheetOpen = false;
          H.classList.remove('lic-sheet-open');
        }
        return;
      }
      var p = parts();
      if (!p || !p.content) return;
      chrome(p);
      if (p.content !== lastContent) {
        lastContent = p.content;
        if (sheetOpen || sp.x !== 0) paintSheet();
      }
      var active = p.sb.classList.contains('active');
      if (active && !sheetOpen && now() < ignoreActiveUntil) return;
      setSheet(active);
    }
    function closeSheet(velocity) {
      var p = parts();
      ignoreActiveUntil = now() + 400;
      setSheet(false, velocity || 0);
      if (!p || !p.sb.classList.contains('active')) return;
      var btn = p.sb.querySelector('[data-mobile-filter-toggle]');
      if (btn) btn.click();
      setTimeout(function () {
        var p2 = parts();
        if (p2 && p2.sb.classList.contains('active') && !sheetOpen) p2.sb.classList.remove('active');
      }, 120);
    }
    function openSheet() {
      var b = q('[data-filters-mobile]');
      if (b) b.click();
      setTimeout(function () {
        var p = parts();
        if (p && !p.sb.classList.contains('active')) p.sb.classList.add('active');
        syncSheet();
        var p3 = parts();
        if (p3 && sheetOpen) {
          var close = p3.sb.querySelector('.drawer-close');
          if (close && D.activeElement && D.activeElement.closest && D.activeElement.closest('.lic-dock')) close.focus({ preventScroll: true });
        }
      }, 60);
    }

    /* Dragging: the header always, the list only when it is scrolled to the top */
    function dragStart(y, t) {
      dragging = true;
      Loop.remove(sheetJob);
      measure(parts());
      drag.y0 = y;
      drag.s0 = sp.x;
      drag.samples = [[t, y]];
    }
    function dragMove(y, t) {
      var v = drag.s0 + (y - drag.y0);
      if (v < 0) v = -rubber(-v, 420);
      sp.x = v;
      sp.v = 0;
      paintSheet();
      drag.samples.push([t, y]);
      if (drag.samples.length > 24) drag.samples.shift();
    }
    function dragEnd(y, t) {
      if (!dragging) return;
      drag.samples.push([t, y]);
      dragging = false;
      if (!sheetOpen) bindTouch(false);
      var v = velocityOf(drag.samples);
      var landing = sp.x + project(v);
      if (landing > closedY * 0.42 || (v > 1100 && sp.x > 12)) {
        closeSheet(v);
      } else {
        sp.config(0.8, 0.38).to(0, v);
        Loop.add(sheetJob);
      }
    }

    on(root, 'pointerdown', function (e) {
      if (!sheetOpen || !mqPhone.matches || e.pointerType === 'mouse' && e.button !== 0) return;
      var hd = e.target.closest('.filter--sidebar-header');
      if (!hd || e.target.closest('button, a, input, label')) return;
      drag.mode = 'pointer';
      drag.id = e.pointerId;
      try { hd.setPointerCapture(e.pointerId); } catch (err) { /* capture is optional */ }
      dragStart(e.clientY, e.timeStamp);
    });
    on(root, 'pointermove', function (e) {
      if (drag.mode !== 'pointer' || e.pointerId !== drag.id || !dragging) return;
      dragMove(e.clientY, e.timeStamp);
    });
    function pointerEnd(e) {
      if (drag.mode !== 'pointer' || e.pointerId !== drag.id) return;
      drag.mode = '';
      dragEnd(e.clientY, e.timeStamp);
    }
    on(root, 'pointerup', pointerEnd);
    on(root, 'pointercancel', pointerEnd);

    /* The list hands its pull to the sheet at the top. These listeners exist only
       while the sheet is open, so they never hold up scrolling of the page. */
    var touch = { y: 0, x: 0, ok: false, last: 0, bound: false };
    function touchStart(e) {
      if (!sheetOpen || e.touches.length !== 1) return;
      var cols = e.target.closest && e.target.closest('.filter--sidebar .filter--columns');
      touch.ok = !!cols && cols.scrollTop <= 0 && !e.target.closest('price-range-bar');
      touch.y = e.touches[0].clientY;
      touch.x = e.touches[0].clientX;
      touch.last = touch.y;
    }
    function touchMove(e) {
      if (!sheetOpen || e.touches.length !== 1) return;
      var t = e.touches[0];
      touch.last = t.clientY;
      if (drag.mode === 'touch' && dragging) {
        if (e.cancelable) e.preventDefault();
        dragMove(t.clientY, e.timeStamp);
        return;
      }
      if (!touch.ok) return;
      var dy = t.clientY - touch.y;
      var dx = t.clientX - touch.x;
      if (dy > 8 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        var cols = e.target.closest('.filter--columns');
        if (cols && cols.scrollTop > 0) {
          touch.ok = false;
          return;
        }
        if (e.cancelable) e.preventDefault();
        drag.mode = 'touch';
        dragStart(touch.y, e.timeStamp);
        dragMove(t.clientY, e.timeStamp);
      } else if (dy < -8) {
        touch.ok = false;
      }
    }
    function touchEnd(e) {
      if (drag.mode !== 'touch') return;
      drag.mode = '';
      dragEnd(touch.last, e.timeStamp);
    }
    function bindTouch(onOff) {
      if (onOff === touch.bound) return;
      touch.bound = onOff;
      var m = onOff ? 'addEventListener' : 'removeEventListener';
      D[m]('touchstart', touchStart, { passive: true });
      D[m]('touchmove', touchMove, { passive: false });
      D[m]('touchend', touchEnd);
      D[m]('touchcancel', touchEnd);
    }
    st.offs.push(function () { bindTouch(false); });

    on(root, 'click', function (e) {
      if (!mqPhone.matches) return;
      if (e.target.closest('[data-lic-cta]')) {
        e.preventDefault();
        closeSheet(0);
        return;
      }
      if (sheetOpen && e.target.classList && e.target.classList.contains('filter--sidebar-overlay')) closeSheet(0);
    });
    on(D, 'keydown', function (e) {
      if (e.key === 'Escape' && sheetOpen) closeSheet(0);
    });

    /* ======================= Floating toolbar (phones) ======================= */
    var dock = null;
    var badges = [];
    var lastBadge = -1;
    function activeFilters() {
      return root.querySelectorAll('[data-applied-filters] > li').length;
    }
    function paintBadges() {
      var n = activeFilters();
      var top = root.querySelector('[data-filters-nav]');
      if (top && !top.querySelector('.lic-badge')) {
        var tb = D.createElement('span');
        tb.className = 'lic-badge';
        tb.hidden = true;
        top.appendChild(tb);
      }
      badges = Array.prototype.slice.call(D.querySelectorAll('.lic-dock .lic-badge, .lic [data-filters-nav] .lic-badge'));
      var s = n ? String(n) : '';
      badges.forEach(function (b) {
        if (b.hidden !== (n === 0)) b.hidden = n === 0;
        if (b.textContent !== s) b.textContent = s;
        if (n !== lastBadge && lastBadge !== -1 && n > 0 && !reduce()) {
          b.classList.add('is-bump');
          setTimeout(function () { b.classList.remove('is-bump'); }, 140);
        }
      });
      lastBadge = n;
    }

    function buildDock() {
      if (root.getAttribute('data-lic-dock') !== 'true' || !q('.collection--topbar')) return;
      var hasFilter = !!q('[data-filters-mobile]');
      var hasSort = !!sortInputs();
      if (!hasFilter && !hasSort) return;
      dock = D.createElement('div');
      dock.className = 'lic-dock';
      dock.setAttribute('dir', RTL ? 'rtl' : 'ltr');
      dock.innerHTML = '<div class="lic-dock-bar" role="toolbar" aria-label="סינון ומיון">' +
        (hasFilter ? '<button type="button" class="lic-dock-btn lic-glass-btn" data-lic-dock-filter>' + ICON.filter + '<span class="lic-dock-t">סינון</span><span class="lic-badge" hidden></span></button>' : '') +
        (hasFilter && hasSort ? '<span class="lic-dock-sep" aria-hidden="true"></span>' : '') +
        (hasSort ? '<button type="button" class="lic-dock-btn lic-glass-btn" data-lic-dock-sort aria-haspopup="menu" aria-expanded="false">' + ICON.sort + '<span class="lic-dock-t">מיון</span></button>' : '') +
        '</div>';
      D.body.appendChild(dock);
      st.offs.push(function () {
        if (dock) dock.remove();
        dock = null;
        H.classList.remove('lic-dock-up');
      });
      W.requestAnimationFrame(function () {
        W.requestAnimationFrame(function () {
          if (!dock) return;
          dock.classList.add('is-in');
          H.classList.add('lic-dock-up');
        });
      });
      on(dock, 'click', function (e) {
        if (e.target.closest('[data-lic-dock-filter]')) openSheet();
      });

      /* Shrinks while scrolling down, grows back on the way up */
      var lastY = W.scrollY;
      var acc = 0;
      var min = false;
      function setMin(v) {
        if (v === min || !dock) return;
        min = v;
        dock.classList.toggle('is-min', v);
      }
      on(W, 'scroll', function () {
        var y = W.scrollY;
        var dy = y - lastY;
        lastY = y;
        if (y < 160) {
          acc = 0;
          return setMin(false);
        }
        if ((dy > 0) !== (acc > 0)) acc = 0;
        acc += dy;
        if (acc > 48) setMin(true);
        else if (acc < -28) setMin(false);
      }, { passive: true });
      on(dock, 'pointerdown', function () { setMin(false); });

      /* Steps aside at the very end of a long page, so the last footer links stay
         reachable; on a short page it never hides */
      var endT = 0;
      function atEnd() {
        if (!dock) return;
        var docH = Math.max(D.documentElement.scrollHeight, D.body.scrollHeight);
        var end = docH > W.innerHeight + 260 && W.innerHeight + W.scrollY >= docH - 28;
        if (dock.classList.contains('is-hidden') !== end) {
          dock.classList.toggle('is-hidden', end);
          H.classList.toggle('lic-dock-up', !end && dock.classList.contains('is-in'));
        }
      }
      on(W, 'scroll', function () {
        if (endT) return;
        endT = W.requestAnimationFrame(function () { endT = 0; atEnd(); });
      }, { passive: true });
    }

    /* ======================= Loox's pop-up rides above the toolbar =======================
       The review card Loox slides in at a bottom corner covered the toolbar and the grid
       on phones. Whatever Loox draws it in (its own box, often an iframe), the fixed box
       around it is lifted above the toolbar and made a little smaller, with a glass edge.
       Its own slide-in keeps working: only translate and scale are used, which add to its
       transform. Its inside (an iframe) cannot be restyled from the page. */
    var LOOX = '[id*="loox" i], [class*="loox" i], iframe[src*="loox" i], iframe[title*="loox" i], iframe[name*="loox" i]';
    var floats = [];
    var looxMO = null;
    var looxTimers = [];
    function fixedBox(el) {
      var top = null;
      for (var n = el; n && n !== D.body && n !== H; n = n.parentElement) {
        if (W.getComputedStyle(n).position === 'fixed') top = n;
      }
      return top;
    }
    function looxLift() {
      floats = floats.filter(function (f) { return f.isConnected; });
      if (!dock || !floats.length) return;
      var gap = parseFloat(W.getComputedStyle(dock).bottom) || 14;
      var need = gap + 54 + 10;
      floats.forEach(function (f) {
        var b = parseFloat(W.getComputedStyle(f).bottom);
        if (isNaN(b)) b = W.innerHeight - f.getBoundingClientRect().bottom;
        f.style.setProperty('--lic-lift', Math.max(0, Math.round(need - b)) + 'px');
      });
    }
    function looxScan() {
      if (!dock || !mqPhone.matches) return;
      var els;
      try { els = D.querySelectorAll(LOOX); } catch (e) { return; }
      var found = false;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (root.contains(el)) continue;
        var f = fixedBox(el);
        if (!f || f.classList.contains('lic-float') || f.contains(dock) || dock.contains(f)) continue;
        var r = f.getBoundingClientRect();
        if (!r.width || !r.height || r.height > W.innerHeight * 0.55) continue;
        if (r.width > W.innerWidth - 8 && r.height > 160) continue;
        var bottom = parseFloat(W.getComputedStyle(f).bottom);
        if (!isNaN(bottom) && bottom > W.innerHeight * 0.4) continue;
        f.classList.add('lic-float');
        f.setAttribute('data-lic-side', r.left + r.width / 2 < W.innerWidth / 2 ? 'l' : 'r');
        floats.push(f);
        found = true;
      }
      if (found) looxLift();
    }
    function looxWatch() {
      if (!dock || looxMO) return;
      var lt = 0;
      looxMO = new MutationObserver(function () {
        clearTimeout(lt);
        lt = setTimeout(looxScan, 120);
      });
      looxMO.observe(D.body, { childList: true });
      [400, 2500, 6000, 12000].forEach(function (ms) { looxTimers.push(setTimeout(looxScan, ms)); });
      looxScan();
      on(W, 'resize', looxLift);
      st.offs.push(function () {
        if (looxMO) looxMO.disconnect();
        looxMO = null;
        looxTimers.forEach(clearTimeout);
        floats.forEach(function (f) {
          f.classList.remove('lic-float');
          f.style.removeProperty('--lic-lift');
        });
        H.classList.remove('lic-dock-up');
      });
    }

    /* ======================= Keep in step with the theme ======================= */
    var scheduled = false;
    function refresh() {
      scheduled = false;
      if (!st.alive) return;
      scanCards(false);
      syncSheet();
      refreshSort();
      paintBadges();
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      Promise.resolve().then(refresh);
    }
    var mo = new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) {
        var r = recs[i];
        if (r.type === 'childList') return schedule();
        var t = r.target;
        if (t.classList && (t.classList.contains('filter--sidebar') || t.hasAttribute('data-products-wrapper'))) return schedule();
      }
    });
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    st.offs.push(function () { mo.disconnect(); });

    function onPhoneChange() {
      if (!mqPhone.matches) {
        var p = parts();
        if (p && p.content) p.content.style.removeProperty('--lic-y');
        if (p && p.overlay) p.overlay.style.removeProperty('--lic-dim');
      }
      syncSheet();
    }
    if (mqPhone.addEventListener) {
      mqPhone.addEventListener('change', onPhoneChange);
      st.offs.push(function () { mqPhone.removeEventListener('change', onPhoneChange); });
    }

    /* The glass setting lives on the section; the toolbar and the menu sit in <body> */
    var glass = W.getComputedStyle(root).getPropertyValue('--lic-glass').trim();
    if (glass) H.style.setProperty('--lic-glass', glass);

    /* First paint: mark the cards already on screen before .lic-ready can hide them */
    H.classList.add('lic-ready');
    scanCards(true);
    buildDock();
    looxWatch();
    refreshSort();
    paintBadges();
    syncSheet();

    return {
      root: root,
      beforeAppend: beforeAppend,
      destroy: function () {
        st.alive = false;
        closeMenu(false);
        Loop.remove(sheetJob);
        Loop.remove(menuJob);
        if (io) io.disconnect();
        clearTimeout(fallbackTimer);
        st.offs.forEach(function (f) { f(); });
        H.classList.remove('lic-sheet-open');
        H.style.removeProperty('--lic-glass');
      }
    };
  }

  /* ------------------------------------------------------------------------
     The theme's infinite scroll: hand each new page to the mounted layer just
     before the theme writes it (see "The next page" in mount)
     ------------------------------------------------------------------------ */
  function patchInfinite() {
    var T = W.theme && W.theme.OnScroll;
    var proto = T && T.prototype;
    if (!proto || typeof proto.fetchFilterData !== 'function') return false;
    if (proto.fetchFilterData.__lic) return true;
    var orig = proto.fetchFilterData;
    var wrapped = function () {
      var p = orig.apply(this, arguments);
      if (!p || typeof p.then !== 'function') return p;
      return p.then(function (text) {
        try {
          if (current && current.beforeAppend) current.beforeAppend();
        } catch (e) { /* never stand in the theme's way */ }
        return text;
      });
    };
    wrapped.__lic = true;
    proto.fetchFilterData = wrapped;
    return true;
  }

  /* ------------------------------------------------------------------------
     Boot, and follow the theme editor
     ------------------------------------------------------------------------ */
  var current = null;
  function boot() {
    var root = D.querySelector('.lic[data-lic]');
    if (!root) return;
    patchInfinite();
    if (current && current.root === root && root.isConnected) return;
    if (current) current.destroy();
    current = mount(root);
  }

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  /* in case the theme's scripts arrive after this one */
  W.addEventListener('load', function () { if (current) patchInfinite(); });

  D.addEventListener('shopify:section:load', function () { boot(); });
  D.addEventListener('shopify:section:unload', function (e) {
    if (current && e.target && e.target.contains(current.root)) {
      current.destroy();
      current = null;
      H.classList.remove('lic-ready');
    }
  });
})();
